import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { billingApi, dayWindow, messagingApi, opsApi } from '@/api/ops';
import type {
  LedgerSummary, MetaPricingReport, ParkedEvent, SalesIqWhatsAppSummary, TenantWhatsAppPanel, WhatsAppConsumptions,
} from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { StatusChip } from '@/components/ops/Badges';
import { ReasonField, TextField, reasonOk } from '@/components/ops/Forms';
import { LoadMore, PageHeader, Section, SubTabs } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState, UnavailablePanel } from '@/components/ops/Problem';
import { TenantName, TenantPicker } from '@/components/ops/TenantPicker';
import { formatCredits } from '@/ops/credits';
import { formatMinor, formatNumber, formatTimestamp, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { useCreditScale } from '@/ops/useCreditScale';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { PanelProblem } from './tenant/UsageFootprintTab';

type Tab = 'parked' | 'refunds' | 'shortfalls' | 'whatsapp' | 'meta';

const bk = (env: string, ...rest: string[]) => ['ops', 'billing', env, ...rest] as const;

/** A day as `YYYY-MM-DD`, `offset` days from today (UTC). */
function isoDay(offset = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));

/**
 * Money operations (money review 2026-10-04): the queues an owner works through by hand — ledger events billing parked
 * (replayed with a reason), payments that must be refunded at the gateway, the credits a refunded pack could not take back —
 * and two per-tenant WhatsApp checks: salesIQ's debits against messaging's lease consumptions and billing's ledger, and Meta's
 * own pricing beside the category we charged. Everything is read live (CP2); a service that is down is its own panel.
 */
export function MoneyOperationsPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && ['parked', 'refunds', 'shortfalls', 'whatsapp', 'meta'].includes(requested) ? requested : 'parked';
  const setTab = (t: Tab) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('tab', t);
    return next;
  }, { replace: true });

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="Money operations">
      <PageHeader title="Money operations" subtitle="What needs a person: parked events, refunds, shortfalls, and the WhatsApp checks."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />} />
      <SubTabs<Tab> label="Money operations" active={tab} onChange={setTab} tabs={[
        { key: 'parked', label: 'Parked events' },
        { key: 'refunds', label: 'Refunds to make' },
        { key: 'shortfalls', label: 'Clawback shortfalls' },
        { key: 'whatsapp', label: 'WhatsApp reconciliation' },
        { key: 'meta', label: 'Meta pricing' },
      ]} />
      {env && tab === 'parked' && <ParkedEventsPanel env={env} />}
      {env && tab === 'refunds' && <RefundsPanel env={env} />}
      {env && tab === 'shortfalls' && <ShortfallsPanel env={env} />}
      {env && (tab === 'whatsapp' || tab === 'meta') && <TenantWindow env={env} tab={tab} />}
    </RequirePermission>
  );
}

// ─── Parked events ────────────────────────────────────────────────────────

export function ParkedEventsPanel({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [source, setSource] = useState('');
  const [replaying, setReplaying] = useState<ParkedEvent | null>(null);
  const [reason, setReason] = useState('');
  const events = useInfiniteQuery({
    queryKey: bk(env, 'parked-events', source.trim()),
    queryFn: ({ pageParam }) => billingApi.parkedEvents(env, { source: source.trim() || undefined, limit: 50, before: pageParam }),
    initialPageParam: null as string | null,
    // Older pages: everything parked before the last row shown.
    getNextPageParam: (last) => (last.events.length >= 50 ? last.events[last.events.length - 1]!.parked_at : undefined),
    ...LIVE,
  });
  const [lastReplay, setLastReplay] = useState<{ ok: boolean; text: string } | null>(null);
  const replay = useMutation({
    mutationFn: () => billingApi.replayParkedEvent(env, replaying!.id, reason.trim()),
    onSuccess: (result) => {
      setLastReplay(result.resolved
        ? { ok: true, text: `Replayed: ${result.outcome}.` }
        : { ok: false, text: `Still parked after ${formatNumber(result.attempts)} attempt(s) — billing could not apply it yet.` });
      setReplaying(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: bk(env, 'parked-events') });
    },
  });

  return (
    <Section title="Parked ledger events" meta="usage or payment events billing could not apply — nothing is lost, nothing is billed until replayed" flush>
      <div className="card-body">
        <div className="filter-grid">
          <TextField label="Source" value={source} onChange={setSource} mono placeholder="salesiq.whatsapp.outbound.sent" />
        </div>
      </div>
      {lastReplay && (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <div className={`callout ${lastReplay.ok ? 'callout-info' : 'callout-warning'}`} role="status">
            <span className="callout-icon" aria-hidden="true">{lastReplay.ok ? 'i' : '!'}</span>
            <div className="t-body">
              {lastReplay.text}
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginInlineStart: 8 }} onClick={() => setLastReplay(null)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}
      <QueryState query={{ ...events, data: events.data }} service="billing">
        {(data) => {
          const rows = data.pages.flatMap((p) => p.events);
          return rows.length === 0 ? <div className="empty-state t-body">Nothing parked.</div> : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Parked</th><th>Source</th><th>Why</th><th>Tenant</th><th className="num">Attempts</th>{canManage && <th aria-label="Actions" />}</tr></thead>
                  <tbody>
                    {rows.map((e) => (
                      <tr key={e.id}>
                        <td className="t-caption">{formatTimestamp(e.parked_at)}<div className="muted">occurred {formatTimestamp(e.occurred_at)}</div></td>
                        <td className="mono t-caption">{e.source}</td>
                        <td className="t-caption wrap">{e.reason}{e.detail && <div className="muted">{e.detail}</div>}</td>
                        <td>{e.tenant_id ? <TenantName env={env} id={e.tenant_id} tab="billing" /> : <span className="muted">—</span>}</td>
                        <td className="num">{formatNumber(e.attempts)}</td>
                        {canManage && (
                          <td><button type="button" className="btn btn-ghost btn-sm" aria-label={`Replay event ${shortId(e.id)}`} onClick={() => setReplaying(e)}>Replay</button></td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <LoadMore hasMore={!!events.hasNextPage} loading={events.isFetchingNextPage} onLoad={() => events.fetchNextPage()} />
            </>
          );
        }}
      </QueryState>
      {replaying && (
        <ConfirmDialog title="Replay this event?" confirmLabel="Replay" pending={replay.isPending} confirmDisabled={!reasonOk(reason)}
          onConfirm={() => replay.mutate()} onCancel={() => { setReplaying(null); setReason(''); replay.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            Billing applies <span className="mono">{replaying.source}</span> again (idempotent — an event already applied is not counted twice).
            It parked because: {replaying.reason}
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={replay.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

// ─── Refunds to make ──────────────────────────────────────────────────────

export function RefundsPanel({ env }: { env: string }) {
  const payments = useQuery({ queryKey: bk(env, 'payments-needs-refund'), queryFn: () => billingApi.paymentsNeedingRefund(env), ...LIVE });
  return (
    <Section title="Payments to refund" meta="money we received that must go back — refund it at the gateway; nothing is refunded from here" flush>
      <QueryState query={payments} service="billing">
        {(data) => data.payments.length === 0 ? <div className="empty-state t-body">No payment needs a refund.</div> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Received</th><th>Tenant</th><th className="num">Amount</th><th>Gateway</th><th>For</th><th>Why</th></tr></thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.id}>
                    <td className="t-caption">{formatTimestamp(p.created_at)}<div className="mono muted">{shortId(p.id)}</div></td>
                    <td><TenantName env={env} id={p.tenant_id} tab="billing" /></td>
                    <td className="num">{formatMinor(p.amount_minor, p.currency)}</td>
                    <td className="mono t-caption">{p.gateway_key}</td>
                    <td className="t-caption">
                      {p.purchase_id ? <>purchase <span className="mono">{shortId(p.purchase_id)}</span></> : null}
                      {p.invoice_id ? <>invoice <span className="mono">{shortId(p.invoice_id)}</span></> : null}
                      {!p.purchase_id && !p.invoice_id && '—'}
                    </td>
                    <td className="t-caption wrap">{p.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </Section>
  );
}

// ─── Clawback shortfalls ──────────────────────────────────────────────────

export function ShortfallsPanel({ env }: { env: string }) {
  const shortfalls = useQuery({ queryKey: bk(env, 'clawback-shortfalls'), queryFn: () => billingApi.clawbackShortfalls(env), ...LIVE });
  const { scale } = useCreditScale(env);
  return (
    <Section title="Clawback shortfalls" meta="a refunded pack takes back what is left; what was already spent is shown here" flush>
      <QueryState query={shortfalls} service="billing">
        {(data) => data.shortfalls.length === 0 ? <div className="empty-state t-body">No shortfalls.</div> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Refunded</th><th>Tenant</th><th>Wallet</th><th className="num">Granted</th><th className="num">Taken back</th><th className="num">Shortfall</th></tr></thead>
              <tbody>
                {data.shortfalls.map((s) => (
                  <tr key={s.purchase_id}>
                    <td className="t-caption">{s.refunded_at ? formatTimestamp(s.refunded_at) : '—'}<div className="mono muted">purchase {shortId(s.purchase_id)}</div></td>
                    <td><TenantName env={env} id={s.tenant_id} tab="billing" /></td>
                    <td className="t-caption">{s.wallet === 'ai' ? 'AI' : s.wallet === 'communications' ? 'SMS & WhatsApp' : s.wallet}</td>
                    <td className="num">{formatCredits(s.credits_granted, scale)}</td>
                    <td className="num">{formatCredits(s.clawed_back_credits, scale)}</td>
                    <td className="num" style={{ color: 'var(--error)', fontWeight: 600 }}>{formatCredits(s.shortfall_credits, scale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </Section>
  );
}

// ─── Per tenant: WhatsApp reconciliation + Meta pricing ───────────────────

function TenantWindow({ env, tab }: { env: string; tab: 'whatsapp' | 'meta' }) {
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenant') ?? '';
  const setTenantId = (id: string) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (id) next.set('tenant', id);
    else next.delete('tenant');
    return next;
  }, { replace: true });
  const [from, setFrom] = useState(isoDay(-29));
  const [to, setTo] = useState(isoDay(0));
  const windowOk = isDay(from) && isDay(to) && from <= to;

  return (
    <>
      <div className="card card-body">
        <div className="filter-grid">
          <TenantPicker env={env} value={tenantId} onChange={setTenantId} placeholder="Choose a tenant" />
          <TextField label="From (UTC day)" value={from} onChange={setFrom} mono placeholder="YYYY-MM-DD" />
          <TextField label="To (UTC day, inclusive)" value={to} onChange={setTo} mono placeholder="YYYY-MM-DD"
            error={isDay(from) && isDay(to) && from > to ? 'From must be on or before To.' : null} />
        </div>
      </div>
      {!tenantId ? (
        <div className="empty-state t-body">Choose a tenant — these checks read one tenant at a time.</div>
      ) : !windowOk ? null : tab === 'whatsapp' ? (
        <WhatsAppReconciliation env={env} tenantId={tenantId} from={from} to={to} />
      ) : (
        <MetaPricingPanel env={env} tenantId={tenantId} from={from} to={to} />
      )}
    </>
  );
}

/** One figure from each of the three ledgers; null = that service could not be read (never a 0). */
export interface ReconRow {
  label: string;
  hint: string;
  salesiq: number | null;
  messaging: number | null;
  billing: number | null;
  /** Credits (formatted with the scale) or a count. */
  kind: 'credits' | 'count';
}

/** True when every source that answered agrees (a missing source is not a mismatch — it is unknown). */
export function rowAgrees(row: ReconRow): boolean {
  const values = [row.salesiq, row.messaging, row.billing].filter((v): v is number => v !== null);
  return values.every((v) => v === values[0]);
}

/**
 * The figures the three services must agree on for one tenant and window (money C8): what was CHARGED (salesIQ's debits with
 * credits &gt; 0 = messaging's consumptions = billing's positive ledger rows) and what was REFUNDED (delivery failures), both as
 * credits and as counts. salesIQ's pending debits are not settled yet, so they are shown apart, never compared.
 */
export function reconcile(
  salesiq: SalesIqWhatsAppSummary | null,
  messaging: WhatsAppConsumptions | null,
  ledger: LedgerSummary | null,
): ReconRow[] {
  const charged = salesiq ? salesiq.charged ?? { count: salesiq.sent.count - salesiq.free.count, credits: salesiq.sent.credits } : null;
  // billing's rows include BYO messages (the tenant pays Meta; no wallet consumption): compare the platform part only —
  // per feature, the row minus its BYO part, then only the features that charged something (a free BYO part sharing a
  // feature with charged platform sends must still come off the count).
  const platform = ledger
    ? ledger.rows
        .map((r) => {
          const byo = (ledger.byo ?? []).filter((b) => b.feature === r.feature);
          return {
            count: r.count - byo.reduce((total, b) => total + b.count, 0),
            price_credits: r.price_credits - byo.reduce((total, b) => total + b.price_credits, 0),
          };
        })
        .filter((r) => r.price_credits > 0)
    : null;
  const billedCredits = platform ? platform.reduce((total, r) => total + r.price_credits, 0) : null;
  const billedCount = platform ? platform.reduce((total, r) => total + r.count, 0) : null;
  return [
    { label: 'Charged', hint: 'credits debited for messages sent', kind: 'credits', salesiq: charged?.credits ?? null, messaging: messaging?.consumed.credits ?? null, billing: billedCredits },
    { label: 'Charged messages', hint: 'how many', kind: 'count', salesiq: charged?.count ?? null, messaging: messaging?.consumed.count ?? null, billing: billedCount },
    { label: 'Refunded', hint: 'failed deliveries given back', kind: 'credits', salesiq: salesiq?.refunded.credits ?? null, messaging: messaging?.refunded.credits ?? null, billing: ledger ? Math.abs(ledger.refunded.price_credits) : null },
    { label: 'Refunded messages', hint: 'how many', kind: 'count', salesiq: salesiq?.refunded.count ?? null, messaging: messaging?.refunded.count ?? null, billing: ledger?.refunded.count ?? null },
  ];
}

function columnError(error: unknown, service: string, env: string) {
  if (error instanceof ApiError && error.isUnavailable) return <UnavailablePanel error={error} service={service} />;
  return (
    <div className="callout callout-error" role="status">
      <span className="callout-icon" aria-hidden="true">✕</span>
      <div className="t-body">{service} could not be read{error instanceof ApiError ? ` (${error.status}${error.code ? ` ${error.code}` : ''})` : ''} — {env}.</div>
    </div>
  );
}

export function WhatsAppReconciliation({ env, tenantId, from, to }: { env: string; tenantId: string; from: string; to: string }) {
  const { scale } = useCreditScale(env);
  const instants = dayWindow(from, to);
  const salesiq = useQuery({
    queryKey: ['ops', 'salesiq', env, 'wa-summary', tenantId, from, to],
    queryFn: () => opsApi.tenants.whatsappSalesIqSummary(env, tenantId, from, to),
    ...LIVE,
  });
  const messaging = useQuery({
    queryKey: ['ops', 'messaging', env, 'wa-consumptions', tenantId, from, to],
    queryFn: () => messagingApi.whatsappConsumptions(env, tenantId, instants.from, instants.to),
    enabled: hasPermission(PERMISSIONS.messagingRead),
    ...LIVE,
  });
  const ledger = useQuery({
    queryKey: bk(env, 'ledger-summary', tenantId, 'whatsapp', from, to),
    queryFn: () => billingApi.ledgerSummary(env, { tenant_id: tenantId, product: 'whatsapp', from: instants.from, to: instants.to }),
    ...LIVE,
  });

  const panel = salesiq.data?.salesiq;
  const summary = panel?.status === 'ok' ? (panel.data as SalesIqWhatsAppSummary | null | undefined) ?? null : null;
  const rows = reconcile(summary, messaging.data ?? null, ledger.data ?? null);
  const fmt = (row: ReconRow, value: number | null) =>
    value === null ? <span className="muted" title="not read">—</span> : row.kind === 'credits' ? formatCredits(value, scale) : formatNumber(value);

  return (
    <div className="stack">
      <Section title="Charged and refunded — three ledgers" meta={`${from} → ${to} (UTC days) · each column is that service's own figure`} flush>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Figure</th><th className="num">salesIQ (debits)</th><th className="num">Messaging (wallet)</th><th className="num">Billing (ledger)</th><th>Check</th></tr></thead>
            <tbody>
              {rows.map((row) => {
                const agrees = rowAgrees(row);
                return (
                  <tr key={row.label}>
                    <td><div style={{ fontWeight: 600 }}>{row.label}</div><div className="t-caption muted">{row.hint}</div></td>
                    <td className="num">{fmt(row, row.salesiq)}</td>
                    <td className="num">{fmt(row, row.messaging)}</td>
                    <td className="num">{fmt(row, row.billing)}</td>
                    <td>{agrees ? <StatusChip status="agrees" tone="success" /> : <StatusChip status="differs" tone="danger" />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="t-caption muted" style={{ margin: 0, padding: '8px 16px 12px' }}>
          A difference near the window's edges can be timing (salesIQ counts a debit when it settles, messaging when it consumes,
          billing when the event arrives); one inside the window is a real gap. A dash is a service that could not be read — never a 0.
        </p>
      </Section>

      <div className="grid-2">
        <Section title="salesIQ" meta="its WhatsApp debits">
          {salesiq.isPending ? <span className="spinner" /> : salesiq.isError ? columnError(salesiq.error, 'salesiq', env)
            : panel && panel.status !== 'ok' ? <PanelProblem panel={panel as TenantWhatsAppPanel<unknown>['salesiq']} env={env} what="WhatsApp summary read" />
              : summary && (
                <ul className="t-body" style={{ margin: 0, paddingInlineStart: 18 }}>
                  <li>Sent {formatNumber(summary.sent.count)} · {formatCredits(summary.sent.credits, scale)} (our cost {formatCredits(summary.sent.cost_credits, scale)})</li>
                  <li>Free (utility in the 24-hour window) {formatNumber(summary.free.count)}</li>
                  <li>Pending, not settled yet {formatNumber(summary.pending.count)} · {formatCredits(summary.pending.credits, scale)}</li>
                  {summary.by_category.map((c) => <li key={c.category}>{c.category || 'unknown'}: {formatNumber(c.count)} · {formatCredits(c.credits, scale)}</li>)}
                </ul>
              )}
        </Section>
        <Section title="Messaging" meta="lease consumptions in the wallet">
          {!hasPermission(PERMISSIONS.messagingRead) ? <span className="t-caption muted">Your role cannot read messaging.</span>
            : messaging.isPending ? <span className="spinner" /> : messaging.isError ? columnError(messaging.error, 'messaging', env) : messaging.data && (
              <ul className="t-body" style={{ margin: 0, paddingInlineStart: 18 }}>
                <li>Consumed {formatNumber(messaging.data.consumed.count)} · {formatCredits(messaging.data.consumed.credits, scale)}</li>
                <li>Settled late (after the lease closed) {formatNumber(messaging.data.late.count)} · {formatCredits(messaging.data.late.credits, scale)}</li>
                {messaging.data.over_consumption && messaging.data.over_consumption.credits > 0 && (
                  <li style={{ color: 'var(--error)' }}>Over a lease&apos;s headroom {formatCredits(messaging.data.over_consumption.credits, scale)}</li>
                )}
              </ul>
            )}
        </Section>
      </div>

      <Section title="Billing ledger by category" meta="WhatsApp rows billing recorded" flush>
        {ledger.isPending ? <div className="empty-state"><span className="spinner" /></div> : ledger.isError ? <div className="card-body">{columnError(ledger.error, 'billing', env)}</div> : ledger.data && (
          ledger.data.rows.length === 0 ? <div className="empty-state t-body">No WhatsApp rows in this window.</div> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Category</th><th className="num">Messages</th><th className="num">Charged</th><th className="num">Our cost</th></tr></thead>
                <tbody>
                  {ledger.data.rows.map((r) => (
                    <tr key={r.feature}>
                      <td className="mono">{r.feature}</td>
                      <td className="num">{formatNumber(r.count)}</td>
                      <td className="num">{formatCredits(r.price_credits, scale)}</td>
                      <td className="num">{formatCredits(r.cost_credits, scale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Section>

      {summary && messaging.data && <LeaseComparison summary={summary} consumptions={messaging.data} scale={scale} />}
    </div>
  );
}

/** Per lease: salesIQ's debits against messaging's consumptions — where a gap is, by lease. */
function LeaseComparison({ summary, consumptions, scale }: { summary: SalesIqWhatsAppSummary; consumptions: WhatsAppConsumptions; scale: number | null }) {
  const leases = new Map<string, { salesiq: number | null; messaging: number | null }>();
  for (const l of summary.by_lease) leases.set(l.lease_id ?? 'none', { salesiq: l.credits, messaging: leases.get(l.lease_id ?? 'none')?.messaging ?? null });
  for (const l of consumptions.by_lease) {
    const k = l.lease_id ?? 'none';
    leases.set(k, { salesiq: leases.get(k)?.salesiq ?? null, messaging: l.credits });
  }
  if (leases.size === 0) return null;
  return (
    <Section title="By lease" meta="salesIQ debits vs messaging consumptions per spending lease" flush>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Lease</th><th className="num">salesIQ</th><th className="num">Messaging</th><th>Check</th></tr></thead>
          <tbody>
            {[...leases.entries()].map(([lease, v]) => (
              <tr key={lease}>
                <td className="mono t-caption">{lease === 'none' ? 'no lease' : shortId(lease)}</td>
                <td className="num">{v.salesiq === null ? '—' : formatCredits(v.salesiq, scale)}</td>
                <td className="num">{v.messaging === null ? '—' : formatCredits(v.messaging, scale)}</td>
                <td>{v.salesiq === v.messaging ? <StatusChip status="agrees" tone="success" /> : <StatusChip status="differs" tone="danger" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/** A Meta pricing row that disagrees with what we charged: another category, or Meta says not billable while we charged. */
export function metaMismatch(row: MetaPricingReport['rows'][number]): string | null {
  if (row.meta_category && row.our_category && row.meta_category.toLowerCase() !== row.our_category.toLowerCase()) {
    return 'Meta priced another category';
  }
  if (row.meta_billable === false && row.credits > 0) return 'Meta did not bill it; we charged';
  if (row.meta_billable === true && row.credits === 0 && row.our_category !== 'service') return 'Meta billed it; we charged nothing';
  return null;
}

export function MetaPricingPanel({ env, tenantId, from, to }: { env: string; tenantId: string; from: string; to: string }) {
  const { scale } = useCreditScale(env);
  const report = useQuery({
    queryKey: ['ops', 'salesiq', env, 'meta-pricing', tenantId, from, to],
    queryFn: () => opsApi.tenants.whatsappMetaPricing(env, tenantId, from, to),
    ...LIVE,
  });
  return (
    <Section title="Our category vs Meta's" meta="Meta's recorded pricing per message — a record; it never changed a charge" flush>
      <QueryState query={report} service="salesiq">
        {(data) => {
          if (data.salesiq.status !== 'ok') return <div className="card-body"><PanelProblem panel={data.salesiq} env={env} what="Meta pricing read" /></div>;
          const rows = (data.salesiq.data as MetaPricingReport | null | undefined)?.rows ?? [];
          return rows.length === 0 ? <div className="empty-state t-body">Meta reported no pricing for this tenant in {data.from} → {data.to}.</div> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Our category</th><th>Meta's category</th><th>Meta billable</th><th>Meta type</th><th className="num">Messages</th><th className="num">We charged</th><th>Check</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    const mismatch = metaMismatch(r);
                    return (
                      <tr key={i} className={mismatch ? 'row-changed' : undefined}>
                        <td className="mono">{r.our_category ?? '—'}</td>
                        <td className="mono">{r.meta_category ?? '—'}</td>
                        <td>{r.meta_billable === null ? '—' : r.meta_billable ? 'yes' : 'no'}</td>
                        <td className="mono t-caption">{r.meta_type ?? '—'}</td>
                        <td className="num">{formatNumber(r.count)}</td>
                        <td className="num">{formatCredits(r.credits, scale)}</td>
                        <td>{mismatch ? <StatusChip status={mismatch} tone="warning" /> : <StatusChip status="same" tone="success" />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }}
      </QueryState>
    </Section>
  );
}
